import { useCallback, useEffect, useState } from 'react';
import type { Route } from '../lib/routes';
import type { AppSession, MeView, PracticeView } from '../lib/types';
import type { AuthConfig } from '../lib/home';
import { ApiError, api } from '../lib/api';
import { displayName } from '../lib/session';
import { HeartMark } from '../components/HeartMark';
import { Toast } from '../components/Bits';
import { AddPatientsPage } from './practice/AddPatientsPage';
import { Dashboard } from './practice/Dashboard';
import { DoctorDashboard } from './practice/DoctorDashboard';
import { EnrollPage } from './practice/EnrollPage';
import { ManagerPatients } from './practice/ManagerPatients';
import { MessagesPage } from './practice/MessagesPage';
import { PatientDetailPage } from './practice/PatientDetailPage';
import { RosterPage } from './practice/RosterPage';
import { PracticeSetup } from './PracticeSetup';

export interface NavItem {
  href: string;
  label: string;
  on: boolean;
  count: number;
  countLabel?: string;
}

/**
 * What each role works on. A clinical manager runs the patient list: who is loaded, invited and
 * enrolled. A doctor follows the patients: how they are doing, whether they are keeping their
 * goals, and what they have written.
 */
export function practiceNav(role: 'doctor' | 'manager', page: Route['page'], practice: PracticeView | null): NavItem[] {
  if (role === 'manager') {
    const waiting = practice?.patientList.filter((p) => p.status === 'pending').length ?? 0;
    return [
      { href: '#/practice', label: 'Patients', on: page === 'practice' || page === 'practice-patient' || page === 'practice-patients', count: waiting, countLabel: `${waiting} not invited yet` },
      { href: '#/practice/add', label: 'Add patients', on: page === 'practice-add' || page === 'practice-enroll', count: 0 },
      { href: '#/practice/roster', label: 'Care team', on: page === 'practice-roster', count: 0 },
    ];
  }
  const openAlerts = practice?.alerts.filter((a) => !a.ackedAt).length ?? 0;
  const unread = practice?.unreadMessages ?? 0;
  return [
    { href: '#/practice', label: 'Dashboard', on: page === 'practice', count: openAlerts, countLabel: `${openAlerts} open alerts` },
    { href: '#/practice/patients', label: 'Patients', on: page === 'practice-patients' || page === 'practice-patient', count: 0 },
    { href: '#/practice/messages', label: 'Messages', on: page === 'practice-messages' || page === 'practice-thread', count: unread, countLabel: `${unread} unread` },
    { href: '#/practice/roster', label: 'Care team', on: page === 'practice-roster', count: 0 },
  ];
}

/** The practice side: a desktop layout with a sidebar, laid out differently for doctors and clinical managers. */
export function PracticeApp({ session, me, config, route, onSignOut, orgReturn, clearOrgReturn, onChanged }: { session: AppSession; me: MeView; config: AuthConfig | null; route: Route; onSignOut: () => void; orgReturn: { code: string; state: string } | null; clearOrgReturn: () => void; onChanged: () => Promise<void> }) {
  const [practice, setPractice] = useState<PracticeView | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [toast, setToast] = useState<string | null>(null);

  const reload = useCallback(async () => {
    try {
      setPractice(await api.practice(session.token));
      setError(null);
    } catch (e) {
      setError(e instanceof ApiError ? e.message : 'Could not load the practice.');
    }
  }, [session.token]);

  useEffect(() => {
    void reload();
    const h = setInterval(() => {
      if (!document.hidden) void reload();
    }, 30_000);
    return () => clearInterval(h);
  }, [reload]);

  if (route.page === 'practice-setup') {
    return <PracticeSetup session={session} config={config} onDone={onChanged} orgReturn={orgReturn} clearOrgReturn={clearOrgReturn} onBack={() => (location.hash = '#/practice')} />;
  }

  const say = (s: string) => setToast(s);
  const role: 'doctor' | 'manager' = me.role === 'doctor' ? 'doctor' : 'manager';
  const nav = practiceNav(role, route.page, practice);

  let body: JSX.Element;
  if (!practice) body = <div className="loading">{error ? <p className="form-error">{error}</p> : <p className="hint">Loading the practice…</p>}</div>;
  else if (route.page === 'practice-patient') body = <PatientDetailPage session={session} me={me} patientId={route.patientId} onChanged={reload} say={say} />;
  else if (route.page === 'practice-roster') body = <RosterPage session={session} me={me} practice={practice} onChanged={reload} say={say} />;
  else if (route.page === 'practice-enroll') body = <EnrollPage session={session} me={me} practice={practice} onChanged={reload} say={say} />;
  else if (role === 'manager') {
    if (route.page === 'practice-add') body = <AddPatientsPage session={session} practice={practice} demo={config?.demoPractice === true} onChanged={reload} say={say} />;
    else body = <ManagerPatients session={session} me={me} practice={practice} onChanged={reload} say={say} />;
  } else {
    if (route.page === 'practice-patients') body = <Dashboard session={session} me={me} practice={practice} onChanged={reload} say={say} />;
    else if (route.page === 'practice-messages' || route.page === 'practice-thread') body = <MessagesPage session={session} practice={practice} patientId={route.page === 'practice-thread' ? route.patientId : null} onChanged={reload} say={say} />;
    else body = <DoctorDashboard session={session} me={me} practice={practice} onChanged={reload} say={say} />;
  }

  return (
    <div className="desk">
      <aside className="side">
        <a className="side-brand" href="#/practice">
          <HeartMark size={26} />
          <span>HeartCoach</span>
        </a>
        <div className="side-practice">
          <strong>{practice?.name ?? me.practice?.name ?? 'Your practice'}</strong>
          <span className="hint">{practice?.orgName ?? me.practice?.orgName ?? ''}</span>
        </div>
        <nav className="side-nav" aria-label={role === 'doctor' ? 'Doctor' : 'Clinical manager'}>
          {nav.map((n) => (
            <a key={n.href} href={n.href} className={n.on ? 'on' : ''} aria-current={n.on ? 'page' : undefined}>
              {n.label}
              {n.count > 0 ? (
                <span className="count" title={n.countLabel}>
                  {n.count}
                </span>
              ) : null}
            </a>
          ))}
        </nav>
        <div className="side-me">
          <strong>{displayName(session)}</strong>
          <span className="hint">
            {me.role === 'doctor' ? 'Doctor' : 'Clinical manager'}
            {me.title ? ` · ${me.title}` : ''}
          </span>
          <button type="button" className="quiet small" onClick={onSignOut}>
            Sign out
          </button>
        </div>
      </aside>
      <main className="desk-main">{body}</main>
      <Toast text={toast} onClose={() => setToast(null)} />
    </div>
  );
}
